import { Square2StackIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Title } from '@radix-ui/react-dialog';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
} from '@/components/core/Dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/core/Table';
import { useTranslation } from '@/hooks/useTranslation';

interface PublicationReportError {
    errorType?: string;
    errorCode?: string;
    errorDescription?: string;
}

interface PublicationErrorDialogProps {
    message: string;
    destination: string;
    date: string;
    errors?: PublicationReportError[];
    onClose: () => void;
}

const PublicationErrorDialog = ({
    message,
    destination,
    date,
    errors = [],
    onClose,
}: PublicationErrorDialogProps) => {
    const { t } = useTranslation();
    const copyLabel = t('PUBLICATION_ERROR.COPY');
    const reportErrors = errors.filter((error) => (
        error.errorType || error.errorCode || error.errorDescription
    ));

    const copyMessage = () => {
        const tableText = reportErrors
            .map((error) => [error.errorType, error.errorCode, error.errorDescription]
                .filter((value) => value)
                .join('\t'))
            .join('\n');
        void navigator.clipboard.writeText(tableText || message);
    };

    return (
        <Dialog open onOpenChange={(next) => {
            if (!next) {
                onClose();
            }
        }}>
            <DialogContent variant="small" className="tw-bg-fill-primary">
                <DialogHeader className="tw-flex tw-w-full">
                    <div className="tw-flex tw-w-full tw-items-center tw-gap-3">
                        <Title asChild>
                            <span className="tw-min-w-0 tw-flex-1 tw-text-t-base tw-font-bold tw-text-h-primary lg:tw-text-t-lg">
                                {t('PUBLICATION_ERROR.REPORT_TITLE')}
                            </span>
                        </Title>
                        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3">
                            <ButtonPanel
                                isDisabled={false}
                                icon={Square2StackIcon}
                                tooltip={copyLabel}
                                aria-label={copyLabel}
                                onClick={copyMessage}
                            />
                            <DialogClose asChild>
                                <ButtonPanel
                                    isDisabled={false}
                                    icon={XMarkIcon}
                                    tooltip={t('CLOSE')}
                                    aria-label={t('CLOSE')}
                                />
                            </DialogClose>
                        </div>
                    </div>
                </DialogHeader>
                <DialogBody scrollable padding="none" className="tw-max-h-[60vh]">
                    <div className="tw-flex tw-flex-col tw-gap-4 tw-px-5 tw-py-8 lg:tw-gap-5 lg:tw-px-8 lg:tw-py-12">
                        {(destination || date) && (
                            <p className="tw-m-0 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">
                                {t('PUBLICATION_ERROR.SUBTITLE', { destination, date })}
                            </p>
                        )}
                        {reportErrors.length > 0 ? (
                            <div className="tw-w-full tw-overflow-auto tw-border tw-relative border-table">
                                <Table className="tw-w-full tw-table-fixed">
                                    <TableHeader>
                                        <TableRow className="table-header-row !tw-border-b-0">
                                            <TableHead className="!tw-h-10 !tw-px-2 !tw-py-2.5 tw-uppercase tw-align-middle" style={{ width: '36%' }}>
                                                {t('PUBLICATION_ERROR.COLUMN_TYPE')}
                                            </TableHead>
                                            <TableHead className="!tw-h-10 !tw-px-2 !tw-py-2.5 tw-uppercase tw-align-middle" style={{ width: '22%' }}>
                                                {t('PUBLICATION_ERROR.COLUMN_CODE')}
                                            </TableHead>
                                            <TableHead className="!tw-h-10 !tw-px-2 !tw-py-2.5 tw-uppercase tw-align-middle">
                                                {t('PUBLICATION_ERROR.COLUMN_DESCRIPTION')}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {reportErrors.map((error, index) => (
                                            <TableRow
                                                key={`${error.errorCode ?? ''}-${index}`}
                                                className="grid-row no-row-selection !tw-h-auto"
                                            >
                                                <TableCell hasPadding className="!tw-h-auto !tw-min-h-[40px] !tw-px-2 !tw-py-2.5 tw-align-middle tw-whitespace-normal">
                                                    <div className="tw-break-words tw-overflow-wrap-anywhere">
                                                        {error.errorType}
                                                    </div>
                                                </TableCell>
                                                <TableCell hasPadding className="!tw-h-auto !tw-min-h-[40px] !tw-px-2 !tw-py-2.5 tw-align-middle tw-whitespace-normal">
                                                    <div className="tw-break-words tw-overflow-wrap-anywhere">
                                                        {error.errorCode}
                                                    </div>
                                                </TableCell>
                                                <TableCell hasPadding className="!tw-h-auto !tw-min-h-[40px] !tw-px-2 !tw-py-2.5 tw-align-middle tw-whitespace-normal">
                                                    <div className="tw-break-words tw-overflow-wrap-anywhere">
                                                        {error.errorDescription}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <p className="tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-t-sm tw-font-normal tw-text-t-primary lg:tw-text-t-base">
                                {message}
                            </p>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary">{t('CLOSE')}</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

PublicationErrorDialog.displayName = 'PublicationErrorDialog';

export { PublicationErrorDialog, type PublicationErrorDialogProps, type PublicationReportError };
